/*
 * Keeps the social-login round trip out of browser Back history (MLC-2674). The provider's own pages (e.g. Google's
 * account chooser) add history entries that no site can remove, so Back after login landed on a stale provider URL
 * ("bad request"). Instead of removing them we jump back over them:
 *   1. markSocialLoginStart     - before leaving, push a marker after the start page and record the start's index.
 *   2. runSocialLoginBridge     - the auth callback goes back to the start entry (same-page destination) or to the
 *                                 marker after it (any other destination) instead of redirecting forward.
 *   3. resumeSocialLoginRedirect - that entry is then replaced by the post-login destination.
 * The destination only ever replaces an entry: a navigation that adds one without a user click makes Chrome skip the
 * page on Back (its back-button-hijacking guard), which sent Back past the start page to the previous document.
 * Any check that fails falls back to a plain redirect, i.e. the behaviour before this change.
 */
import { sessionQueryParams } from "@/apiUtils/constants";

export const SOCIAL_LOGIN_START_KEY = "socialLoginStart";
export const SOCIAL_LOGIN_PENDING_KEY = "socialLoginPending";
/** A start marker older than this belongs to an abandoned login and is ignored. */
export const SOCIAL_LOGIN_START_TTL_MS = 10 * 60 * 1000;
/** The start page resumes right after the bridge goes back to it; an older pending login is left over and ignored. */
export const SOCIAL_LOGIN_PENDING_TTL_MS = 2 * 60 * 1000;
/** Browsers cap session history at 50 entries; at the cap old entries drop off and indexes shift. */
export const SESSION_HISTORY_CAP = 50;
/** Last resort if history.go() never leaves the bridge page. */
export const SOCIAL_LOGIN_BRIDGE_FALLBACK_MS = 5000;

/** Login-modal params that must not survive on the page we return to. */
const LOGIN_MODAL_PARAMS = [sessionQueryParams.LOGIN_MODAL_STATUS, sessionQueryParams.IS_CHECKOUT];

/**
 * Records the page a social login starts from. Call it right before navigating to the federation endpoint with
 * location.assign(): the marker entry pushed here must stay in history, because the post-login destination replaces it.
 */
export const markSocialLoginStart = (win: Window) => {
  try {
    const url = new URL(win.location.href);
    LOGIN_MODAL_PARAMS.forEach((key) => url.searchParams.delete(key));
    const startUrl = url.pathname + url.search + url.hash;

    // Clean the entry we return to, then push a marker: the push drops any forward entries, so the start entry's
    // index is exactly history.length - 2 and the marker's is history.length - 1.
    win.history.replaceState(win.history.state, "", startUrl);
    win.history.pushState(win.history.state, "", startUrl);

    if (win.history.length >= SESSION_HISTORY_CAP) {
      win.sessionStorage.removeItem(SOCIAL_LOGIN_START_KEY);
    } else {
      win.sessionStorage.setItem(
        SOCIAL_LOGIN_START_KEY,
        JSON.stringify({ index: win.history.length - 2, url: startUrl, at: Date.now() }),
      );
    }
  } catch (error) {
    try {
      win.sessionStorage.removeItem(SOCIAL_LOGIN_START_KEY);
    } catch (storageError) {}
  }
};

/**
 * Runs on the auth-callback bridge page. It is inlined there with toString(), so it must stay self-contained:
 * no imports, no outer variables, only its arguments.
 */
export function runSocialLoginBridge(
  win: Window,
  redirectUrl: string,
  startKey: string,
  pendingKey: string,
  ttlMs: number,
  historyCap: number,
  fallbackMs: number,
  locales: readonly string[] = [],
) {
  const redirect = () => {
    try {
      win.sessionStorage.removeItem(pendingKey);
    } catch (error) {}
    win.location.replace(redirectUrl);
  };
  // Path without the locale prefix or a trailing slash, so /sa-en/cart and /cart are the same page.
  const toPagePath = (url: string) => {
    const pathname = new URL(url, win.location.href).pathname;
    const [, firstSegment = "", ...rest] = pathname.split("/");
    const isLocale = locales.some((locale) => locale.toLowerCase() === firstSegment.toLowerCase());
    return (isLocale ? `/${rest.join("/")}` : pathname).replace(/\/+$/, "") || "/";
  };
  try {
    const raw = win.sessionStorage.getItem(startKey);
    win.sessionStorage.removeItem(startKey);
    const start = raw ? JSON.parse(raw) : null;
    const isUsable =
      !!start &&
      typeof start.index === "number" &&
      start.index >= 0 &&
      typeof start.url === "string" &&
      typeof start.at === "number" &&
      Date.now() - start.at < ttlMs &&
      win.history.length < historyCap;
    if (isUsable) {
      // Same page (cart -> cart?mergeCart=true): the start entry itself is replaced, so Back goes to the page before
      // it. Another page (cart -> checkout): the marker after the start entry is replaced, so Back returns to the start.
      const target =
        toPagePath(redirectUrl) === toPagePath(start.url) ? start.index : start.index + 1;
      const steps = win.history.length - 1 - target;
      if (steps >= 1) {
        win.sessionStorage.setItem(
          pendingKey,
          JSON.stringify({ url: redirectUrl, startUrl: start.url, at: Date.now() }),
        );
        win.setTimeout(redirect, fallbackMs);
        win.history.go(-steps);
        return;
      }
    }
  } catch (error) {}
  redirect();
}

// Escape "<" and JS line separators so values can't break out of the inline <script>.
const toScriptSafe = (value: unknown) => {
  const BS = String.fromCharCode(92); // literal backslash
  const LS = String.fromCharCode(0x2028);
  const PS = String.fromCharCode(0x2029);
  return JSON.stringify(value ?? "")
    .split("<")
    .join(BS + "u003c")
    .split(LS)
    .join(BS + "u2028")
    .split(PS)
    .join(BS + "u2029");
};

// Escape a value for an HTML attribute.
const toAttributeSafe = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * The auth callback's bridge page: runs runSocialLoginBridge inline under a nonce CSP, with a <noscript> fallback to
 * the plain redirect. Kept here, free of Next.js imports, so it can be unit tested.
 */
export const buildSocialLoginBridgePage = (
  redirectUrl: string,
  scriptNonce: string,
  locales: readonly string[],
) => {
  const bridgeArgs = [
    toScriptSafe(redirectUrl),
    toScriptSafe(SOCIAL_LOGIN_START_KEY),
    toScriptSafe(SOCIAL_LOGIN_PENDING_KEY),
    SOCIAL_LOGIN_START_TTL_MS,
    SESSION_HISTORY_CAP,
    SOCIAL_LOGIN_BRIDGE_FALLBACK_MS,
    toScriptSafe(locales),
  ].join(", ");

  return {
    html: `<!DOCTYPE html>
      <html>
        <head>
          <meta name="referrer" content="no-referrer" />
          <script nonce="${scriptNonce}">(${runSocialLoginBridge.toString()})(window, ${bridgeArgs});</script>
          <noscript><meta http-equiv="refresh" content="0;url=${toAttributeSafe(redirectUrl)}" /></noscript>
        </head>
        <body></body>
      </html>`,
    headers: {
      "content-type": "text/html",
      // Per-login page: never cache or index it.
      "Cache-Control": "no-store",
      "X-Robots-Tag": "noindex",
      // Its URL carries the auth code and state; keep it out of the next page's Referer and analytics.
      "Referrer-Policy": "no-referrer",
      "X-Frame-Options": "SAMEORIGIN",
      "Content-Security-Policy": `default-src 'none'; script-src 'nonce-${scriptNonce}'; base-uri 'none'; object-src 'none'; frame-ancestors 'self'`,
    },
  };
};

/**
 * Path + query without login-modal params or a trailing slash, with params sorted, so a re-opened modal or a reordered
 * query does not hide the start page.
 */
const toComparableUrl = (url: URL) => {
  const search = new URLSearchParams(url.search);
  LOGIN_MODAL_PARAMS.forEach((key) => search.delete(key));
  search.sort();
  const query = search.toString();
  return (url.pathname.replace(/\/+$/, "") || "/") + (query ? `?${query}` : "");
};

/**
 * Finishes a social login on the entry the bridge went back to (the start page, or the marker after it) by replacing
 * that entry with the destination. Never assign(): an entry added without a user click is skipped by Chrome's Back
 * button, and so is the start page with it. Returns true when it navigated.
 */
export const resumeSocialLoginRedirect = (win: Window): boolean => {
  let pending: { url?: unknown; startUrl?: unknown; at?: unknown } | null;
  try {
    const raw = win.sessionStorage.getItem(SOCIAL_LOGIN_PENDING_KEY);
    if (!raw) return false;
    win.sessionStorage.removeItem(SOCIAL_LOGIN_PENDING_KEY);
    pending = JSON.parse(raw);
  } catch (error) {
    return false;
  }
  if (typeof pending?.url !== "string" || typeof pending?.startUrl !== "string") return false;
  if (typeof pending.at !== "number" || Date.now() - pending.at >= SOCIAL_LOGIN_PENDING_TTL_MS) {
    return false;
  }

  let target: URL;
  let start: URL;
  try {
    target = new URL(pending.url, win.location.href);
    start = new URL(pending.startUrl, win.location.href);
  } catch (error) {
    return false;
  }
  if (!["http:", "https:"].includes(target.protocol)) return false;
  // Only the start page (or its marker, which has the same URL) may continue the login; anywhere else it is stale.
  if (toComparableUrl(start) !== toComparableUrl(new URL(win.location.href))) return false;

  win.location.replace(target.href);
  return true;
};
